import { useEffect, useRef } from 'react';

import { FieldPath, useFormContext, useWatch } from 'react-hook-form';

import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { z } from 'zod';

import { cn } from 'components/lib/utils';
import { BlockMetadata } from 'components/TemplatesStudio/DesignsMetadata/types';

import { templateStudioSchema } from '../../../validation-schema';

import { DesignPreviewSlider } from './DesignPreviewSlider';

const DesignTitle: React.FC<{ name: string }> = ({ name }) => {
  return (
    <div className="flex items-center justify-start w-full text-center text-xs font-bold text-base-mutedForeground h-[20px]">
      {name}
    </div>
  );
};

const NewBadge: React.FC = () => (
  <div
    className={cn(
      'new-badge flex items-center px-2 py-1 text-xs rounded-full absolute top-[10px] right-[9px] bg-base-card shadow-md w-[47px] h-[20px] justify-center align-middle'
    )}
  >
    <span className={cn('text-green-500')}>{'New'}</span>
  </div>
);

interface IDesignSidebarItemProps extends BlockMetadata {
  blockDesignIdPath: FieldPath<z.infer<typeof templateStudioSchema>>;
  onDesignSelect?: (designId: string) => void;
}

export const DesignSidebarItem: React.FC<IDesignSidebarItemProps> = ({
  id,
  previewAssets,
  name,
  blockDesignIdPath,
  isNew,
  onDesignSelect
}) => {
  const isDragging = useRef(false);

  const { control, setValue } =
    useFormContext<z.infer<typeof templateStudioSchema>>();
  const { displayPopupPreview, setDisplayPopupPreview, openSidebarBlock } =
    useTemplateStudioState();

  const selectedDesignId = useWatch({
    control,
    name: blockDesignIdPath
  });

  const handleDesignSelect = (designId: string) => {
    if (!blockDesignIdPath || isDragging.current) return;

    setValue(blockDesignIdPath, designId, {
      shouldValidate: true,
      shouldDirty: true
    });

    if (!displayPopupPreview && openSidebarBlock === 'popup') {
      setDisplayPopupPreview(true);
    }

    onDesignSelect?.(designId);
  };

  const setIsDragging = (isDraggingNow: boolean) => {
    isDragging.current = isDraggingNow;
  };

  return (
    <div
      key={id}
      className="flex flex-col gap-1 w-[192px] cursor-pointer"
      onClick={() => handleDesignSelect(id)}
    >
      <div
        className={cn(
          `design-previews-slider-container relative h-[195px] w-[192px] bg-neutral-100 rounded-lg p-3 border hover:shadow-[0_1px_3px_0_rgba(0,0,0,0.10),_0_1px_2px_0_rgba(0,0,0,0.06)]`,
          selectedDesignId === id ? 'border-[#818CF8]' : 'border-transparent'
        )}
      >
        {isNew && <NewBadge />}
        <DesignPreviewSlider
          assets={previewAssets}
          setIsDragging={setIsDragging}
        />
      </div>
      <DesignTitle name={name} />
    </div>
  );
};
